/**
 * Dentis Promo Status block hydration.
 *
 * Listens for `dentis:discount-resolved` and:
 *   - If a code is active and the current product is eligible (or block is set
 *     to render even when ineligible), reveals the block, replaces placeholders
 *     `{code}`, `{savings_percent}`, `{savings_amount}`, `{shipping_benefit}`.
 *   - Otherwise, leaves the block hidden.
 */
(function () {
  'use strict';

  var EVENT_NAME = 'dentis:discount-resolved';
  var DBG = (window.DentisDebug || { enabled: false, log: function () {}, warn: function () {}, error: function () {} });

  function withThousands(intStr, sep) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    var amount = Number(cents) / 100;
    if (Math.abs(amount - Math.round(amount)) < 0.005) {
      return withThousands(String(Math.round(amount)), ' ') + ' kr';
    }
    return withThousands(String(Math.floor(amount)), ' ') + ',' + (amount.toFixed(2).split('.')[1]) + ' kr';
  }

  function getProductEligibility(resolved, productId) {
    if (!resolved || !resolved.active) return null;
    if (resolved.type !== 'percentage' && resolved.type !== 'fixed') return null;
    var bucket = resolved.eligible && resolved.eligible[String(productId || '')];
    if (bucket) {
      var perVariant = 0;
      for (var k in bucket.variants) {
        if (Number(bucket.variants[k]) > perVariant) perVariant = Number(bucket.variants[k]);
      }
      return {
        total: Number(bucket.savings_total || 0),
        perVariant: perVariant
      };
    }
    // Heuristic: target_selection === 'all' codes apply to every product.
    if (resolved.target_selection === 'all' && (resolved.target_type === 'line_item' || !resolved.target_type)) {
      return { total: 0, perVariant: 0, fromAllSelector: true };
    }
    return null;
  }

  function computeAmountCents(resolved, currentPriceCents, perVariantSavingsCents) {
    if (!resolved) return 0;
    // Prefer formula-derived savings tied to the currently displayed variant
    // so the announcement always matches what the bundle picker shows. Falls
    // back to the engine's per-variant cart-derived savings only if no
    // current variant price is available (defensive — block always renders
    // data-dentis-current-price from Liquid).
    if (resolved.type === 'percentage' && Number(resolved.value) > 0 && currentPriceCents > 0) {
      return Math.round(Number(currentPriceCents) * Number(resolved.value) / 100);
    }
    if (resolved.type === 'fixed' && Number(resolved.value) > 0) {
      return Math.round(Number(resolved.value) * 100);
    }
    return Number(perVariantSavingsCents || 0);
  }

  // Compose cumulative savings + active code list across all kept subs in
  // resolved.discounts. The announcement is a product-level summary, so it
  // lists ALL kept sub codes from the engine — not just those the composer
  // matched for one specific variant. The composer is only used for the
  // cumulative {savings_amount} / {savings_percent}, with a best-effort
  // variant fallback so cores that restrict_to_variants still contribute
  // when no specific variant id is passed in.
  function composeStack(resolved, productId, basePriceCents, variantId) {
    var subs = (resolved && Array.isArray(resolved.discounts) && resolved.discounts.length > 0)
      ? resolved.discounts
      : (resolved ? [resolved] : []);
    if (subs.length === 0) return { codes: [], percentEffective: 0, amountCents: 0 };

    var allCodes = subs.map(function (s) { return s.code || ''; }).filter(function (c) { return !!c; });

    var po = window.DentisPriceOverride;
    if (po && typeof po.compose === 'function' && basePriceCents > 0) {
      // Prefer the caller-provided variant id. If absent, sniff any variant
      // id out of the subs' eligible buckets so cores tied to specific
      // variants still get composed in the cumulative math.
      var vid = String(variantId || '');
      if (!vid) {
        for (var si = 0; si < subs.length; si++) {
          var bucket = subs[si].eligible && subs[si].eligible[String(productId || '')];
          if (bucket && bucket.variants) {
            var keys = Object.keys(bucket.variants);
            if (keys.length > 0) { vid = keys[0]; break; }
          }
        }
      }
      var composed = po.compose(resolved, basePriceCents, productId, vid);
      if (composed && composed.applied && composed.applied.length > 0) {
        var pct = basePriceCents > 0 ? Math.round((composed.savingsCents / basePriceCents) * 100) : 0;
        return { codes: allCodes, percentEffective: pct, amountCents: composed.savingsCents };
      }
    }
    // Composer unavailable or didn't match anything — still surface all codes
    // and the primary's percent so the announcement stays meaningful.
    var primary = subs[0] || {};
    var primaryPct = primary.type === 'percentage' ? Math.round(Number(primary.value || 0)) : 0;
    return { codes: allCodes, percentEffective: primaryPct, amountCents: 0 };
  }

  function fillTemplate(template, resolved, perVariantSavingsCents, currentPriceCents, productId, variantId, options) {
    if (!template) return '';
    var multiList = !!(options && options.multiList);
    var code = (resolved && resolved.code) || '';
    var pct = '';
    var amt = '';
    var shipping = '';
    var codesJoined = code;

    // The placeholder the merchant picks ({savings_percent} vs
    // {savings_amount}) is only an output format - it must render a correct
    // value no matter what type the discount itself is. So derive BOTH the
    // amount and the percent from the same underlying savings, rather than
    // gating each format on a matching discount type. computeAmountCents
    // already normalizes percentage codes (base * pct) and fixed codes
    // (value) into a savings amount in cents.
    var amountCents = computeAmountCents(resolved, currentPriceCents, perVariantSavingsCents);
    if (amountCents > 0) {
      amt = formatMoney(amountCents);
    }

    if (resolved && resolved.type === 'percentage' && Number(resolved.value) > 0) {
      // Authoritative percent straight from the code definition.
      pct = String(Math.round(Number(resolved.value)));
    } else if (amountCents > 0 && currentPriceCents > 0) {
      // Fixed-amount (or otherwise) code: compute the effective percent from
      // the actual savings against the current variant price.
      pct = String(Math.round(amountCents / currentPriceCents * 100));
    }

    if (resolved && resolved.type === 'shipping') {
      shipping = 'gratis frakt';
    }

    // Multi-sub awareness: when more than one sub is present, the {codes}
    // placeholder joins all kept codes and the cumulative savings amount /
    // effective percent comes from the matrix composer. {code} stays as
    // the primary for back-compat.
    //
    // multiList=true switches `{codes}` rendering to a newline-separated
    // bulleted list ("- CODE_A\n- CODE_B") so the block can show stacked
    // codes on individual lines via the dedicated multi-code template. The
    // surrounding element needs `white-space: pre-line` for the line breaks
    // to render — handled by the `dentis-promo-status--multi` modifier
    // class toggled in processBlock.
    var subs = (resolved && Array.isArray(resolved.discounts)) ? resolved.discounts : [];
    if (subs.length > 1) {
      var stack = composeStack(resolved, productId, currentPriceCents, variantId);
      if (stack.codes.length > 0) {
        codesJoined = multiList
          ? stack.codes.map(function (c) { return '- ' + c; }).join('\n')
          : stack.codes.join(' + ');
      }
      if (stack.amountCents > 0) amt = formatMoney(stack.amountCents);
      if (stack.percentEffective > 0) pct = String(stack.percentEffective);
    }

    return template
      .replace(/\{codes\}/g, codesJoined)
      .replace(/\{code\}/g, code)
      .replace(/\{savings_percent\}/g, pct)
      .replace(/\{savings_amount\}/g, amt)
      .replace(/\{shipping_benefit\}/g, shipping);
  }

  // Resolve the active template + multi-list flag for a block based on how
  // many kept sub-codes the resolved state actually has. When multiple subs
  // are present AND the block has a non-empty `data-dentis-template-multi`,
  // use that template with multiList rendering. Otherwise fall back to the
  // single-code template.
  function selectTemplate(block, resolved) {
    var single = block.getAttribute('data-dentis-template') || '';
    var multi = block.getAttribute('data-dentis-template-multi') || '';
    var subs = (resolved && Array.isArray(resolved.discounts)) ? resolved.discounts : [];
    if (subs.length > 1 && multi.trim() !== '') {
      return { template: multi, multiList: true };
    }
    return { template: single, multiList: false };
  }

  function readCurrentPrice(block) {
    var raw = block.getAttribute('data-dentis-current-price')
           || block.getAttribute('data-dentis-fallback-price')
           || '0';
    var parsed = parseInt(raw, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  function processBlock(block, resolved) {
    var productId = block.getAttribute('data-dentis-product-id');
    var variantId = block.getAttribute('data-dentis-current-variant-id') || '';
    var textTarget = block.querySelector('[data-dentis-promo-text]');
    var currentPrice = readCurrentPrice(block);
    DBG.log('processBlock', { block: block, productId: productId, hasTextTarget: !!textTarget, currentPrice: currentPrice, resolved: resolved });
    if (!textTarget) {
      DBG.warn('promo block has no [data-dentis-promo-text] child; skipping');
      return;
    }

    if (!resolved || !resolved.active) {
      DBG.log('no active discount; hiding promo block');
      block.setAttribute('hidden', '');
      return;
    }

    var picked = selectTemplate(block, resolved);
    if (picked.multiList) {
      block.classList.add('dentis-promo-status--multi');
    } else {
      block.classList.remove('dentis-promo-status--multi');
    }
    var opts = { multiList: picked.multiList };

    if (resolved.type === 'shipping') {
      textTarget.textContent = fillTemplate(picked.template, resolved, 0, currentPrice, productId, variantId, opts);
      block.removeAttribute('hidden');
      return;
    }

    if (resolved.type !== 'percentage' && resolved.type !== 'fixed') {
      // BXGY etc. — show static "code active" status using the active template
      textTarget.textContent = fillTemplate(picked.template, resolved, 0, currentPrice, productId, variantId, opts);
      block.removeAttribute('hidden');
      return;
    }

    var elig = getProductEligibility(resolved, productId);
    var suppressWhenIneligible = block.getAttribute('data-dentis-suppress-when-ineligible') === '1';
    if (!elig) {
      if (suppressWhenIneligible) {
        block.setAttribute('hidden', '');
        return;
      }
      // Render generic active text without product-specific savings
      textTarget.textContent = fillTemplate(picked.template, resolved, 0, currentPrice, productId, variantId, opts);
      block.removeAttribute('hidden');
      return;
    }

    textTarget.textContent = fillTemplate(picked.template, resolved, elig.perVariant, currentPrice, productId, variantId, opts);
    block.removeAttribute('hidden');
  }

  function processAll(resolved) {
    var blocks = document.querySelectorAll('[data-dentis-promo-status]');
    DBG.log('processAll: found ' + blocks.length + ' [data-dentis-promo-status] blocks');
    for (var i = 0; i < blocks.length; i++) processBlock(blocks[i], resolved);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Cart drawer discount banner — Liquid pre-fills it when Shopify already has
  // the discount in cart.cart_level_discount_applications (order-level codes
  // attach even on an empty cart). Product-level / entitled automatic
  // discounts never land in that array on an empty / non-entitled cart, so
  // this fills the banner from the engine-resolved code instead. The
  // data-dentis-source attribute on the codes wrapper is the conflict guard:
  //   - 'liquid' => Shopify-attached; the JS never overwrites or hides this
  //   - 'js'     => filled by this function; safe to re-render or clear
  //   - absent   => empty placeholder; safe to fill
  function processCartDiscountBanner(resolved) {
    var banners = document.querySelectorAll('[data-dentis-cart-discount-banner]');
    if (!banners.length) return;
    DBG.log('processCartDiscountBanner: found ' + banners.length + ' banner(s)', resolved);
    for (var i = 0; i < banners.length; i++) {
      var banner = banners[i];
      var codesEl = banner.querySelector('[data-dentis-cart-discount-codes]');
      if (!codesEl) continue;

      var source = codesEl.getAttribute('data-dentis-source');
      if (source === 'liquid') {
        // Order-level path: Shopify cart already has the discount. Leave alone.
        banner.removeAttribute('hidden');
        continue;
      }

      if (resolved && resolved.active && resolved.code) {
        codesEl.innerHTML = '<strong class="dentis-cart-discount-banner__code">'
          + escapeHtml(resolved.code) + '</strong>';
        codesEl.setAttribute('data-dentis-source', 'js');
        banner.removeAttribute('hidden');
      } else if (source === 'js') {
        // Previously JS-sourced but discount no longer active — clear & hide.
        codesEl.innerHTML = '';
        codesEl.removeAttribute('data-dentis-source');
        banner.setAttribute('hidden', '');
      } else {
        banner.setAttribute('hidden', '');
      }
    }
  }

  function handle(resolved) {
    try {
      processAll(resolved);
      processCartDiscountBanner(resolved);
    } catch (e) { DBG.error('handle() threw', e); }
  }

  document.addEventListener(EVENT_NAME, function (ev) {
    handle((ev && ev.detail) || window.DentisActiveDiscount);
  });

  // After cart-sync re-renders the cart drawer, the banner DOM is fresh.
  // Re-sync the banner from the last known resolved state so a product-level
  // code that isn't in cart_level_discount_applications still shows up.
  document.addEventListener('dentis:cart-drawer:updated', function () {
    try { processCartDiscountBanner(window.DentisActiveDiscount || null); }
    catch (e) { DBG.error('cart-drawer:updated handler threw', e); }
  });

  // Keep {savings_amount} in sync with the bundle picker by re-rendering
  // promo-status blocks whenever the selected variant changes. The bundle
  // picker dispatches `variant:changed` on `document` with `e.detail.variant`
  // (full Shopify variant object — price is in cents).
  document.addEventListener('variant:changed', function (ev) {
    var variant = ev && ev.detail && ev.detail.variant;
    if (!variant) return;
    var nextPrice = Number(variant.price);
    if (!isFinite(nextPrice) || nextPrice <= 0) return;
    var blocks = document.querySelectorAll('[data-dentis-promo-status]');
    for (var i = 0; i < blocks.length; i++) {
      blocks[i].setAttribute('data-dentis-current-price', String(nextPrice));
      if (variant.id) blocks[i].setAttribute('data-dentis-current-variant-id', String(variant.id));
    }
    if (window.DentisActiveDiscount) {
      DBG.log('variant:changed -> re-rendering promo blocks with price', nextPrice);
      handle(window.DentisActiveDiscount);
    }
  });

  function maybeReplay() {
    if (window.DentisActiveDiscount) {
      DBG.log('replay: window.DentisActiveDiscount already set on listener attach', window.DentisActiveDiscount);
      handle(window.DentisActiveDiscount);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeReplay);
  } else {
    setTimeout(maybeReplay, 0);
  }
})();
